import { useEffect, useMemo, useState } from "react";
import { usePostHog } from "@posthog/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { BusinessListingStatus, CreateBusinessListingRequest, BusinessListingResponse } from "../../types/businessListingTypes";
import {
  captureBusinessListingDraftSaved,
  captureBusinessListingPublicationCheckoutStarted,
  captureBusinessListingPublished,
} from "../../lib/analytics";
import { businessListingsApi } from "../../lib/businessListings";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import {
  formatBusinessListingCompensation,
  formatBusinessListingDuration,
  formatBusinessListingJobType,
} from "../../utils/BusinessListingUtils";
import { formatCityStateZip } from "../../utils/LocationUtils";
import PageShell from "../ui/Page/PageShell";
import Toast, { type ToastState } from "../ui/Toast/Toast";
import PageBackButton from "../ui/Page/PageBackButton";
import PageHero from "../ui/Page/PageHero";
import PageSectionCard from "../ui/Page/PageSectionCard";
import PageActionBar from "../ui/Page/PageActionBar";
import Button from "../ui/Button/Button";
import Pagination from "../gigs/Pagination";
import Modal from "../ui/Modal/Modal";
import CreateBusinessListingModal from "./CreateBusinessListingModal";
import { useAuth } from "../../hooks/useAuth";

type StatusFilter = "ALL" | BusinessListingStatus;
type StripePublicationReturnState = {
  result: string;
  listingId: string | null;
};

const toReadableDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const listingStatusLabel: Record<BusinessListingStatus, string> = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
  EXPIRED: "Expired",
  CLOSED: "Closed",
};
const PAGE_SIZE_OPTIONS = [5, 10, 20] as const;

const isLivePublishedListing = (listing: BusinessListingResponse) =>
  listing.status === "PUBLISHED"
  && listing.expiresAt !== null
  && new Date(listing.expiresAt).getTime() > Date.now();

const volunteerApprovalCopy = {
  PENDING: {
    title: "Volunteer posting approval pending",
    body: "Your nonprofit volunteer posting request is under review. Paid business listings still use the normal checkout flow.",
  },
  APPROVED: {
    title: "Volunteer posting review approved",
    body: "Your organization is approved for nonprofit volunteer posting. Paid business listings still use the normal checkout flow.",
  },
  REJECTED: {
    title: "Volunteer posting review not approved",
    body: "Your nonprofit volunteer posting request was not approved. Paid business listings still use the normal checkout flow.",
  },
} as const;

const BusinessListingDetailsContent = ({
  listing,
}: {
  listing: BusinessListingResponse;
}) => {
  const listingIsLive = isLivePublishedListing(listing);
  const isEffectivelyExpired = listing.status === "EXPIRED"
    || (listing.status === "PUBLISHED" && !listingIsLive);

  return (
    <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0 rounded-xl border border-myteengig-border bg-white p-4 text-center md:text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Description</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary [overflow-wrap:anywhere]">
            {listing.description}
          </p>
        </div>
        <div className="min-w-0 rounded-xl border border-myteengig-border bg-white p-4 text-center md:text-left">
          <dl className="space-y-3 text-sm text-myteengig-text-primary">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Compensation</dt>
              <dd className="mt-1 font-medium [overflow-wrap:anywhere]">
                {formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Location</dt>
              <dd className="mt-1 [overflow-wrap:anywhere]">{formatCityStateZip(listing)}</dd>
            </div>
            {listing.estimatedDurationMinutes ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Usual shift</dt>
                <dd className="mt-1 [overflow-wrap:anywhere]">{formatBusinessListingDuration(listing.estimatedDurationMinutes)}</dd>
              </div>
            ) : null}
            {listing.expiresAt && listing.status !== "DRAFT" ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">
                  {isEffectivelyExpired ? "Expired" : "Live until"}
                </dt>
                <dd className="mt-1 [overflow-wrap:anywhere]">{toReadableDate(listing.expiresAt)}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Updated</dt>
              <dd className="mt-1 [overflow-wrap:anywhere]">{toReadableDate(listing.updatedAt)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

const BusinessListingsWorkspace = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState<BusinessListingResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubmittingCreateListing, setIsSubmittingCreateListing] = useState(false);
  const [editingListing, setEditingListing] = useState<BusinessListingResponse | null>(null);
  const [isSubmittingEditListing, setIsSubmittingEditListing] = useState(false);
  const [repostingListing, setRepostingListing] = useState<BusinessListingResponse | null>(null);
  const [closingListingId, setClosingListingId] = useState<string | null>(null);
  const [pendingCloseListing, setPendingCloseListing] = useState<BusinessListingResponse | null>(null);
  const [stripePublicationReturn, setStripePublicationReturn] = useState<StripePublicationReturnState | null>(null);
  const [detailsModalListing, setDetailsModalListing] = useState<BusinessListingResponse | null>(null);
  const volunteerApprovalStatus = user?.businessVolunteerApprovalStatus ?? "NOT_REQUESTED";
  const volunteerApprovalMessage = volunteerApprovalStatus === "PENDING"
    || volunteerApprovalStatus === "APPROVED"
    || volunteerApprovalStatus === "REJECTED"
    ? volunteerApprovalCopy[volunteerApprovalStatus]
    : null;

  const loadListings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setListings(await businessListingsApi.listMine());
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to load business listings."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadListings();
  }, []);

  useEffect(() => {
    const publicationStatus = searchParams.get("businessListingCheckout");
    const listingId = searchParams.get("listingId");
    if (!publicationStatus) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("businessListingCheckout");
    nextParams.delete("listingId");
    setSearchParams(nextParams, { replace: true });
    setStripePublicationReturn({ result: publicationStatus, listingId });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!stripePublicationReturn) {
      return;
    }

    if (stripePublicationReturn.result === "cancel") {
      void loadListings();
      setStatusFilter("DRAFT");
      setToast({
        tone: "info",
        message: "Checkout was canceled. The listing was saved as a draft.",
      });
      setStripePublicationReturn(null);
      return;
    }

    if (!stripePublicationReturn.listingId) {
      setStripePublicationReturn(null);
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const loadPublicationStatus = async () => {
      try {
        const listing = await businessListingsApi.getById(stripePublicationReturn.listingId!);
        if (cancelled) {
          return;
        }
        if (listing.publicationPaymentStatus === "CHECKOUT_PENDING" && attempts < 4) {
          attempts += 1;
          window.setTimeout(() => {
            void loadPublicationStatus();
          }, 2000);
          return;
        }

        await loadListings();
        if (listing.status === "PUBLISHED") {
          captureBusinessListingPublished(posthog, {
            listing_id: listing.id,
            source: "business_listings_workspace",
          });
          setStatusFilter("PUBLISHED");
          setToast({
            tone: "success",
            message: "Listing published. It will stay live for 3 months.",
          });
        } else {
          setStatusFilter("DRAFT");
          setToast({
            tone: "info",
            message: "Checkout finished, but the listing is not live yet. Refresh again if Stripe just returned.",
          });
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiErrorToMessage(requestError, "Unable to load the business listing publication status."));
        }
      } finally {
        if (!cancelled) {
          setStripePublicationReturn(null);
        }
      }
    };

    void loadPublicationStatus();
    return () => {
      cancelled = true;
    };
  }, [posthog, stripePublicationReturn]);

  const filteredListings = useMemo(() => {
    if (statusFilter === "ALL") {
      return listings;
    }
    return listings.filter((listing) => listing.status === statusFilter);
  }, [listings, statusFilter]);
  const pageCount = Math.ceil(filteredListings.length / pageSize);
  const safePage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const paginatedListings = filteredListings.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );
  const hasPreviousPage = safePage > 0;
  const hasNextPage = safePage + 1 < pageCount;

  useEffect(() => {
    setPage(0);
  }, [statusFilter, pageSize]);

  const handleCreateListing = async (payload: CreateBusinessListingRequest) => {
    setIsSubmittingCreateListing(true);
    setToast(null);
    setError(null);

    try {
      if ((payload.status ?? "PUBLISHED") === "DRAFT") {
        const listing = await businessListingsApi.createDraft(payload);
        captureBusinessListingDraftSaved(posthog, {
          listing_id: listing.id,
          source: "business_listings_workspace",
          category: payload.category,
          job_type: payload.jobType,
          pay_type: payload.payType,
          min_pay_amount_cents: payload.minPayAmountCents,
          max_pay_amount_cents: payload.maxPayAmountCents,
        });
        await loadListings();
        setIsCreateModalOpen(false);
        setStatusFilter("DRAFT");
        setToast({
          tone: "success",
          message: "Listing saved as draft.",
        });
        return;
      }

      const response = await businessListingsApi.createPublicationCheckoutSession(payload);
      if (!response.checkoutRequired || response.published) {
        captureBusinessListingPublished(posthog, {
          listing_id: response.listingId,
          source: "business_listings_workspace",
        });
        await loadListings();
        setIsCreateModalOpen(false);
        setRepostingListing(null);
        setStatusFilter("PUBLISHED");
        setToast({
          tone: "success",
          message: "Volunteer listing published. It will stay live for 3 months.",
        });
        return;
      }

      if (!response.checkoutUrl) {
        throw new Error("Checkout was not available for this listing.");
      }

      captureBusinessListingPublicationCheckoutStarted(posthog, {
        source: "business_listings_workspace",
        category: payload.category,
        job_type: payload.jobType,
        pay_type: payload.payType,
        min_pay_amount_cents: payload.minPayAmountCents,
        max_pay_amount_cents: payload.maxPayAmountCents,
      });
      window.location.assign(response.checkoutUrl);
    } catch (requestError) {
      throw new Error(apiErrorToMessage(requestError, "Failed to create business listing."));
    } finally {
      setIsSubmittingCreateListing(false);
    }
  };

  const handleCloseListing = async (listingId: string) => {
    setClosingListingId(listingId);
    setToast(null);
    setError(null);
    try {
      await businessListingsApi.closeListing(listingId);
      await loadListings();
      setDetailsModalListing((current) => (current?.id === listingId ? null : current));
      setStatusFilter("CLOSED");
      setToast({
        tone: "success",
        message: "Listing closed. New applicants will no longer see it.",
      });
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to close business listing."));
    } finally {
      setClosingListingId(null);
      setPendingCloseListing(null);
    }
  };

  const handleUpdateListing = async (payload: CreateBusinessListingRequest) => {
    if (!editingListing) {
      return;
    }

    setIsSubmittingEditListing(true);
    setToast(null);
    setError(null);
    try {
      await businessListingsApi.updateListing(editingListing.id, {
        ...payload,
        status: editingListing.status,
      });
      await loadListings();
      setEditingListing(null);
      setToast({
        tone: "success",
        message: "Listing updated.",
      });
    } catch (requestError) {
      throw new Error(apiErrorToMessage(requestError, "Failed to update business listing."));
    } finally {
      setIsSubmittingEditListing(false);
    }
  };

  return (
    <PageShell width="6xl">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <Modal
        isOpen={pendingCloseListing !== null}
        onClose={() => {
          if (!closingListingId) {
            setPendingCloseListing(null);
          }
        }}
        title="Are you sure you want to close this listing?"
        panelClassName="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            Once you close this listing, new applicants will no longer be able to apply and you will not be able to re-open this listing.
          </p>
          {pendingCloseListing ? (
            <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Listing</p>
              <p className="mt-1 text-sm font-semibold text-myteengig-text-primary">{pendingCloseListing.title}</p>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={closingListingId !== null}
              onClick={() => setPendingCloseListing(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!pendingCloseListing || closingListingId !== null}
              onClick={() => {
                if (pendingCloseListing) {
                  void handleCloseListing(pendingCloseListing.id);
                }
              }}
            >
              {closingListingId !== null ? "Closing..." : "Yes, close listing"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={detailsModalListing !== null}
        onClose={() => setDetailsModalListing(null)}
        title={detailsModalListing ? `Listing Details: ${detailsModalListing.title}` : "Listing Details"}
        panelClassName="max-w-4xl h-[min(90vh,52rem)]"
        bodyClassName="overflow-y-auto"
      >
        {detailsModalListing ? <BusinessListingDetailsContent listing={detailsModalListing} /> : null}
      </Modal>
      <PageBackButton fallbackTo="/dashboard" label="Back to Dashboard" className="mb-4" />
      <PageHero
        title={<span className="text-2xl sm:text-3xl">Job Listings</span>}
        description="Create and manage business job listings without the gig funding and schedule-negotiation flow."
        className="bg-gradient-to-br from-white via-myteengig-background to-white py-3 sm:py-4 lg:py-4"
        contentClassName="[&_h1]:mt-0 [&_h1+div]:mt-3"
        footer={(
          <div className="flex justify-center">
            <Button type="button" size="sm" className="w-full sm:w-auto" onClick={() => setIsCreateModalOpen(true)}>
              Create Job Listing
            </Button>
          </div>
        )}
      />

      {volunteerApprovalMessage ? (
        <div className="mt-6 rounded-xl border border-myteengig-border bg-white px-5 py-4 shadow-sm">
          <p className="text-sm font-semibold text-myteengig-text-primary">{volunteerApprovalMessage.title}</p>
          <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{volunteerApprovalMessage.body}</p>
        </div>
      ) : null}

      <div className="mt-8">
        <PageSectionCard className="bg-white/90 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="lg:w-56 lg:shrink-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">
                Filter listings
              </p>
              <p className="mt-1 max-w-sm text-xs leading-5 text-myteengig-text-muted lg:max-w-none">
                Review all listings or narrow the view to published openings or drafts you still need to finish.
              </p>
            </div>
            <PageActionBar align="end" className="flex-1 sm:items-center lg:justify-end">
              {[
                { label: "All", value: "ALL" as const },
                { label: "Published", value: "PUBLISHED" as const },
                { label: "Closed", value: "CLOSED" as const },
                { label: "Expired", value: "EXPIRED" as const },
                { label: "Drafts", value: "DRAFT" as const },
              ].map((filter) => {
                const isActive = statusFilter === filter.value;
                return (
                  <Button
                    key={filter.value}
                    type="button"
                    variant={isActive ? "primary" : "outline"}
                    onClick={() => setStatusFilter(filter.value)}
                  >
                    {filter.label}
                  </Button>
                );
              })}
              <div className="hidden min-w-0 md:block">
                <label htmlFor="business-listings-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                  Rows
                </label>
                <select
                  id="business-listings-page-size"
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                  className="h-[42px] rounded-lg border border-myteengig-border bg-white px-3 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </PageActionBar>
          </div>
        </PageSectionCard>
      </div>

      <div className="mt-6 space-y-4">
        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
        ) : null}

        {isLoading ? (
          <PageSectionCard className="p-5">
            <p className="text-sm text-myteengig-text-muted">Loading job listings...</p>
          </PageSectionCard>
        ) : filteredListings.length === 0 ? (
          <PageSectionCard className="p-5">
            <h2 className="text-xl font-semibold text-myteengig-text-primary">No listings in this view</h2>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              Create a listing or change filters to review other business postings.
            </p>
          </PageSectionCard>
        ) : (
          paginatedListings.map((listing) => {
            const listingIsLive = isLivePublishedListing(listing);
            const isEffectivelyExpired = listing.status === "EXPIRED"
              || (listing.status === "PUBLISHED" && !listingIsLive);
            const canEditListing = listing.status === "DRAFT" || listingIsLive;
            const canRepostListing = isEffectivelyExpired;
            const statusLabel = isEffectivelyExpired ? "Expired" : listingStatusLabel[listing.status];

            return (
              <PageSectionCard key={listing.id} className="p-5">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-myteengig-text-primary">{listing.title}</h2>
                  <p className="mt-2 text-sm font-semibold text-myteengig-text-primary">
                    {formatBusinessListingCompensation(listing.payType, listing.minPayAmountCents, listing.maxPayAmountCents, listing.jobType)}
                  </p>
                  <p className="mt-2 text-sm font-medium text-myteengig-text-muted">
                    {formatBusinessListingJobType(listing.jobType)} • {listing.category}
                  </p>
                  <div className="mt-3 flex justify-center">
                    <span className="rounded-full border border-myteengig-border px-3 py-1 text-xs font-semibold text-myteengig-text-primary">
                      {statusLabel}
                    </span>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    {canEditListing ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditingListing(listing)}
                      >
                        Edit Listing
                      </Button>
                    ) : null}
                    {canRepostListing ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRepostingListing(listing)}
                      >
                        Repost Listing
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDetailsModalListing(listing)}
                    >
                      View Details
                    </Button>
                    {listing.status !== "DRAFT" ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/adult/business-listings/${listing.id}/applicants`)}
                      >
                        Review Applicants
                      </Button>
                    ) : null}
                    {listingIsLive ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={closingListingId === listing.id}
                        onClick={() => setPendingCloseListing(listing)}
                      >
                        {closingListingId === listing.id ? "Closing..." : "Close Listing"}
                      </Button>
                    ) : null}
                  </div>
                </div>

              </PageSectionCard>
            );
          })
        )}
      </div>

      {!isLoading && filteredListings.length > 0 ? (
        <Pagination
          page={safePage}
          pageSize={pageSize}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          resultsCount={paginatedListings.length}
          onPageChange={setPage}
        />
      ) : null}

      <CreateBusinessListingModal
        isOpen={isCreateModalOpen}
        isSubmitting={isSubmittingCreateListing}
        onClose={() => {
          if (!isSubmittingCreateListing) {
            setIsCreateModalOpen(false);
          }
        }}
        onSubmit={handleCreateListing}
      />
      <CreateBusinessListingModal
        isOpen={editingListing !== null}
        isSubmitting={isSubmittingEditListing}
        initialValues={editingListing}
        modalTitle="Edit Job Listing"
        publishLabel="Save Changes"
        publishingLabel="Saving Changes..."
        allowDraftAction={false}
        chargesPublicationFee={false}
        primaryActionStatus={editingListing?.status ?? "DRAFT"}
        onClose={() => {
          if (!isSubmittingEditListing) {
            setEditingListing(null);
          }
        }}
        onSubmit={handleUpdateListing}
      />
      <CreateBusinessListingModal
        isOpen={repostingListing !== null}
        isSubmitting={isSubmittingCreateListing}
        initialValues={repostingListing}
        modalTitle="Repost Job Listing"
        publishLabel="Pay $19.99 & Repost"
        publishingLabel="Starting Checkout..."
        allowDraftAction={false}
        primaryActionStatus="PUBLISHED"
        onClose={() => {
          if (!isSubmittingCreateListing) {
            setRepostingListing(null);
          }
        }}
        onSubmit={handleCreateListing}
      />
    </PageShell>
  );
};

export default BusinessListingsWorkspace;
